/**
 * DayCell Component
 *
 * Base cell component for calendar grids. Provides the visual container
 * with status-based coloring, selection states, and interaction handling.
 *
 * Used as the foundation for MonthDayCell, YearDayCell, and WeekDayCell.
 *
 * @example
 * ```tsx
 * <DayCell status="fullyBooked" size="md" onClick={handleClick}>
 *   <span>8</span>
 * </DayCell>
 * ```
 */

import { Icon } from "@/components/ui/display/Icon";
import classNames from "classnames";
import React, { useCallback } from "react";
import styles from "./DayCell.module.css";
import type { DayCellProps } from "./types";

export const DayCell: React.FC<DayCellProps> = ({
  status = "free",
  size = "md",
  colored = true,
  isWeekend = false,
  isToday = false,
  selected = false,
  selectable = false,
  inRangePreview = false,
  disabled = false,
  onClick,
  absenceFill = 0,
  children,
  className,
  "data-testid": testId,
  "data-component-name": componentName,
}) => {
  // Passes event for modifier key detection (shift, ctrl, meta)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Only handle click if we have an onClick handler
      // Otherwise let the event bubble to parent components
      if (!disabled && onClick) {
        e.stopPropagation(); // Prevent background click from closing sidebar
        onClick(e);
      }
    },
    [disabled, onClick],
  );

  // Keyboard handler - creates synthetic event for modifier key detection
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && !disabled && onClick) {
        e.preventDefault();
        e.stopPropagation();
        // Create a synthetic mouse event with keyboard modifiers
        const syntheticEvent = {
          shiftKey: e.shiftKey,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          stopPropagation: () => {},
          preventDefault: () => {},
        } as React.MouseEvent;
        onClick(syntheticEvent);
      }
    },
    [disabled, onClick],
  );

  const isInteractive = !disabled && onClick;
  const isClickable = !selectable && isInteractive;
  const normalizedAbsenceFill = Math.min(1, Math.max(0, absenceFill));
  const showAbsenceFill = normalizedAbsenceFill > 0 && status !== "absence";
  const isFullAbsenceFill = normalizedAbsenceFill >= 1;

  return (
    <div
      className={classNames(styles.cell, className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      style={
        showAbsenceFill
          ? ({
              "--absence-fill": normalizedAbsenceFill.toFixed(4),
            } as React.CSSProperties)
          : undefined
      }
      data-testid={testId}
      data-component-name={componentName}
      data-size={size}
      data-status={status}
      data-colored={colored ? "true" : "false"}
      data-weekend={isWeekend ? "true" : "false"}
      data-today={isToday ? "true" : "false"}
      data-selectable={selectable ? "true" : "false"}
      data-selected={selected ? "true" : "false"}
      data-in-range-preview={inRangePreview ? "true" : "false"}
      data-clickable={isClickable ? "true" : "false"}
      data-disabled={disabled ? "true" : "false"}
    >
      {children}

      {/* Selection icons for selectable mode - using bold check for crisp small rendering */}
      {selectable && (
        <span data-component-name="check-icon">
          <Icon name="check-bold" size="sm" />
        </span>
      )}

      {/* Absence fill gradient indicator */}
      {showAbsenceFill && (
        <span
          data-component-name="absence-fill"
          data-full-absence-fill={isFullAbsenceFill ? "true" : "false"}
        />
      )}
    </div>
  );
};

DayCell.displayName = "DayCell";

export default DayCell;
